/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useParams } from 'react-router-dom';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

// Component imports
import { Button, Col, Row, Space, Spin, Typography, Upload } from 'antd';
import { CheckCircleOutlined, CloseOutlined } from '@ant-design/icons';

// Misc import
import { MAX_UPLOAD_FILE_SIZE, MEDIA_UPLOAD_TYPES } from '../../constants/main';
import { transformFileSize } from '../../utils/misc/transformFileSize';
import { useCreateMediaFileMutation } from '../../store/media/mediaApi';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

// Constant declarations
const { Dragger } = Upload;
const { Text } = Typography;

const MediaUpload = ({
  hasCloseButton = true,
  isUploadVisible,
  isPublicToOrg,
  setIsUploadVisible = () => {},
  onUploadComplete = () => {},
  showInfoText = true
}) => {
  const { t } = useTranslation();
  const { orgId } = useParams();

  const [isUploading, setIsUploading] = useState(false);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [createMediaFile] = useCreateMediaFileMutation({ fixedCacheKey: 'mediaFiles-create' });
  const { currentUser } = useSelector((state) => state.auth);

  const [attachment, setAttachment] = useState();

  const openUpload = () => {
    setIsUploadVisible(true);
    setIsUploading(false);
    setIsUploadComplete(false);
  };

  const openUploading = () => {
    setIsUploadVisible(false);
    setIsUploading(true);
    setIsUploadComplete(false);
  };

  const openUploadComplete = () => {
    setIsUploadVisible(false);
    setIsUploading(false);
    setIsUploadComplete(true);
  };

  const closeUpload = () => {
    setIsUploadVisible(false);
    setIsUploading(false);
    setIsUploadComplete(false);
  };

  const handleUpload = async (data) => {
    const { file } = data;

    if (file.size > MAX_UPLOAD_FILE_SIZE) {
      openConsoleAlertMessage({ type: 'error', message: t('The maximum allowed upload size is {{fileSize}}', { fileSize: transformFileSize(MAX_UPLOAD_FILE_SIZE) }) });
    } else {
      openUploading();
      const formData = new FormData();
      formData.append('media', file);
      if (isPublicToOrg === false) {
        formData.append('createdByUserId', currentUser.id);
        formData.append('publicToOrg', 'false');
      } else {
        formData.append('publicToOrg', 'true');
      }

      const saveResult = await createMediaFile({ orgId, data: formData });
      if (saveResult?.data?.id) {
        openUploadComplete();
        onUploadComplete(saveResult.data.url);
        setTimeout(() => {
          openUpload();
        }, 3000);
      } else {
        openUpload();
      }
    }
  };

  const renderCloseButton = () => {
    return hasCloseButton ? (
      <Button
        icon={<CloseOutlined />}
        className="float-right mr-5"
        onClick={(e)=> {
          e.stopPropagation();
          closeUpload();
        }}>
      </Button>
    ) : null;
  };

  return (
    <Fragment>
      {
        isUploadComplete || isUploadVisible || isUploading ? (
          <Dragger
            listType="picture"
            accept={Object.values(MEDIA_UPLOAD_TYPES)}
            disabled= {isUploading || isUploadComplete}
            className="mb-5 h-auto"
            beforeUpload={(file) => {
              const fileIsValid = Object.values(MEDIA_UPLOAD_TYPES).includes(file.type);
              if (!fileIsValid) {
                openConsoleAlertMessage({ type: 'error', message: t('{{fileName}} is not a valid file', { fielName: file.name }) });
              }
              setAttachment(file);
              return fileIsValid || Upload.LIST_IGNORE;
            }}
            customRequest={handleUpload}
            showUploadList={false}
          >
            {
              isUploading ? (
                <Fragment>
                  {renderCloseButton()}

                  <Row className="w-100" align="middle" justify="center">
                    <Col>
                      <Text strong className="fs-lg">{t('Uploading')} "{attachment.name}"</Text>
                    </Col>
                  </Row>

                  <Row className="w-100" align="middle" justify="center">
                    <Col flex="auto" className="text-center">
                      <Spin className="mt-10 mb-8"/>
                    </Col>
                  </Row>
                </Fragment>
              ) : null
            }

            {
              isUploadComplete ? (
                <Fragment>
                  {renderCloseButton()}

                  <Row className="w-100 mb-5" align="middle" justify="center">
                    <Col>
                      <Space direction="vertical">
                        <Text strong italic className="fs-lg">"{attachment.name}"</Text>
                        <Text strong className="fs-lg">{t('Successfully Uploaded!')} </Text>
                      </Space>
                    </Col>
                  </Row>

                  <Row className="w-100" align="middle" justify="center">
                    <Col flex="auto" className="text-center">
                      <CheckCircleOutlined className="text-success fs-xxxxl" />
                    </Col>
                  </Row>
                </Fragment>
              ) : null
            }

            {
              !isUploadComplete && !isUploading ? (
                <Fragment>
                  {renderCloseButton()}

                  <Row gutter={[16, 16]} align="middle" justify="center" className="mt-5 mb-4">
                    <Col span={24} className="text-center">
                      <Space size={32}>
                        <Button size="large">{t('Select')}</Button>
                        <Text>{t('or')}</Text>
                        <Text strong>{t('Drop File to this area')}</Text>
                      </Space>
                    </Col>

                    {showInfoText ? (
                      <Col span={24} className="text-center">
                        <Text type="secondary">
                          {t('Support for a single upload. Maximum upload file size: 5MB')}
                        </Text>
                      </Col>
                    ) : null}
                  </Row>
                </Fragment>
              ) : null
            }
          </Dragger>
        ) : null
      }
    </Fragment>
  );
};

export default MediaUpload;

MediaUpload.propTypes = {
  isUploadVisible: PropTypes.bool,
  setIsUploadVisible: PropTypes.func,
  hasCloseButton: PropTypes.bool,
  onUploadComplete: PropTypes.func,
  isPublicToOrg: PropTypes.bool,
  showInfoText: PropTypes.bool
};
